import React, {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {NOTIFICATION_SETTINGS} from 'shared/account-settings/actions/notification-settings';
import ContactInfoForm from 'shared/account-settings/components/ContactInfoForm';
import AccountSettings, {
  AccountSettingsTabComponent,
  Tab,
} from 'src/components/AccountSettings/AccountSettings';
import {SharedAccountSettings} from 'src/components/AccountSettings/AccountSettingsContext';
import {ACCOUNT_SETTING_ROUTES} from 'src/components/AccountSettings/utils/utils';
import AccountConversion from 'src/views/high-school/account-settings/components/account-conversion';
import {STUDENT_FIELDS} from '../../../../shared/account-settings/actions';
import {submitForm} from '../../../user/actions';
import {getMedia} from '../../../utils/mq';
import Connections from './containers/connections';
import ProfileForm from './profile-form/profile-form';
import PrivacyForm from './PrivacyForm';
import {showHSAccountConversionTab} from './utils';

import {HighSchoolInfo} from 'src/types/education';
import {HighSchoolAccountSettingsService as SettingsService} from 'src/services/account-settings-service';
import {
  Ethnicity,
  Gender,
  IsUsCitizenOrResident,
  Sex,
} from 'src/components/AccountSettings/profile-form/utils';

export type HighSchoolAccountSettings = SharedAccountSettings & {
  _id: string;
  firstName: string;
  parentEmail: string;
  lastName: string;
  gender: Gender;
  sex: Sex;
  usCitizenOrResident?: IsUsCitizenOrResident;
  religion: string;
  isFirstGen: boolean;
  ethnicity: Ethnicity;
  addressLine1: string;
  addressLine2: string;
  city: string;
  subdivision: string;
  country: string;
  postalCode: string;
  birthdayDay: number;
  birthdayMonth: number;
  birthdayYear: number;
  currentInstitution: string;
  confirmedUnverifiedAddress: boolean;
  addressVerified: boolean;
  autoConnectCounselors: boolean;
  shareDataWithAllSchools: boolean;
  highSchoolInfo: HighSchoolInfo;
};

const tabs: Tab<HighSchoolAccountSettings>[] = [
  {
    ...ACCOUNT_SETTING_ROUTES.profile,
    component: function ProfileTab({
      settings,
      saveSettings,
      ...props
    }: AccountSettingsTabComponent<HighSchoolAccountSettings>) {
      return <ProfileForm initialValues={settings} onSubmit={saveSettings} {...props} />;
    },
  },
  {
    ...ACCOUNT_SETTING_ROUTES.contact,
    component: function ContactInfoTab({
      settings,
      saveSettings,
      setNavigationAnnouncement,
      ...props
    }: AccountSettingsTabComponent<HighSchoolAccountSettings>) {
      return (
        <ContactInfoForm
          initialValues={settings}
          // @ts-expect-error
          onSubmit={saveSettings}
          hideParentEmail={false}
          setNavigationAnnouncement={setNavigationAnnouncement}
          {...props}
        />
      );
    },
  },
  {
    ...ACCOUNT_SETTING_ROUTES.privacy,
    component: () => (
      <PrivacyForm connectionSettingTitle="Connect automatically with educators at my school" />
    ),
  },
  {
    ...ACCOUNT_SETTING_ROUTES.connect,
    component: function ConnectTab({
      settings,
      saveSettings,
      setNavigationAnnouncement,
      ...props
    }: AccountSettingsTabComponent<HighSchoolAccountSettings>) {
      return (
        <Connections
          initialValues={settings}
          onSubmit={saveSettings}
          setNavigationAnnouncement={setNavigationAnnouncement}
          description="Connect with the counselors, teachers and/or parents who reach out to you. They want to
          help you prepare for college! Connecting enables them to safely see your portfolio,
          colleges that you follow and your earnings at those colleges. They will also see your
          email, but not your address."
          {...props}
        />
      );
    },
  },
];

const fetchAccountSettings = (): Promise<HighSchoolAccountSettings> =>
  SettingsService.show('me', {fields: `${STUDENT_FIELDS},notificationSettings`});

const HighSchoolAccountSettings = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    SettingsService.show('me', {
      fields: 'highSchoolInfo',
    }).then(({highSchoolInfo}) => {
      if (showHSAccountConversionTab(highSchoolInfo?.graduationYear)) {
        tabs.push({
          ...ACCOUNT_SETTING_ROUTES.convert,
          component: function ConversionTab({
            settings,
            saveSettings,
            setNavigationAnnouncement,
            ...props
          }: AccountSettingsTabComponent<HighSchoolAccountSettings>): React.ReactElement {
            return (
              <AccountConversion
                // @ts-expect-error: initialValues does not exist on type
                initialValues={settings}
                onSubmit={saveSettings}
                setNavigationAnnouncement={setNavigationAnnouncement}
                {...props}
              />
            );
          },
        });
      }
    });
  }, []);

  return (
    <AccountSettings<HighSchoolAccountSettings>
      tabs={tabs}
      fetchAccountSettings={fetchAccountSettings}
      availableNotificationSettings={NOTIFICATION_SETTINGS}
      saveSettings={(
        newSettings: Partial<HighSchoolAccountSettings>,
        previousSettings?: HighSchoolAccountSettings | null
      ): Promise<HighSchoolAccountSettings> => {
        const shouldValidateAddress =
          !previousSettings?.confirmedUnverifiedAddress && !previousSettings?.addressVerified;
        return submitForm(
          newSettings,
          '/v1/users',
          getMedia().MS_AND_DOWN,
          dispatch,
          shouldValidateAddress,
          true,
          true
        );
      }}
    />
  );
};

export default HighSchoolAccountSettings;
